import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TruckJourneyService } from '../../services/truck-journey.service';
import { TruckJourneyAssignmentLoad, TruckJourneyDetail, TruckJourneyLoadEdit } from '../../models/truck-journey.model';
import { StopType } from '../../../../shared/stop-type.model';
import { finalize, map } from 'rxjs/operators';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { LoaderService } from '../../../../shared/services/loader.service';
import { ComponentCanDeactivateByField } from '../../../../core/guards/pending-changes-action.guard';
import { TruckJourneyRequestMapper } from '../../mappers/truck-journey-request.mapper';
import { CarrierService } from '../../../../user-dashboard/shared/carrier.service';
import { AbstractEntityEditionComponent } from '../../../../shared/components/astract-entity-edition.component';
import { NavigationService } from '../../../../shared/services/navigation.service';
import { TranslatedToastrService } from '../../../../shared/services/translated-toastr.service';
import { TruckJourneyLoadMapper } from '../../mappers/truck-journey-load.mapper';
import { ModalComponent } from '../../../../core/modal/modal.component';
import { TranslateService } from '@ngx-translate/core';
import { iif, noop, Observable } from 'rxjs';
import * as moment from 'moment';
import { NgForm } from '@angular/forms';
import { isEqual } from 'lodash';
import { AuthenticationService } from '../../../../shared/services/authentication.service';

@Component({
  selector: 'app-truck-journey-edition',
  templateUrl: './truck-journey-edition.component.html',
  styleUrls: ['truck-journey-edition.component.scss']
})
export class TruckJourneyEditionComponent extends AbstractEntityEditionComponent<TruckJourneyDetail> implements OnInit, ComponentCanDeactivateByField {

  @ViewChild('appModal', {static: true}) public appModal: ModalComponent;
  @ViewChild('deleteModalContent', {static: true}) public deleteModalContent: TemplateRef<any>;
  @ViewChild('goBackModal', {static: true}) public goBackModalContent: TemplateRef<any>;
  @ViewChild('truckjourneyForm') public truckjourneyForm: NgForm;

  public preventNavigationFrom = false;
  public editingLoadIndex = -1;
  public formErrorMessageKey: string;
  public carrierId: string = null;
  public regionId: string = null;
  public loadAssignments: TruckJourneyAssignmentLoad[] = [];
  public isShowingLoadAssignment = false;
  public isEditingOrder = false;
  public yesterday = moment().startOf('day').add(-1, 'days');
  public originalLoadIds: string[] = [];
  public unassignedLoadsNumber = 0;
  public isLoading: Observable<boolean>;

  protected readonly noop = noop;
  protected readonly StopType = StopType;

  constructor(
    protected route: ActivatedRoute,
    private carrierService: CarrierService,
    private truckJourneyService: TruckJourneyService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private navigation: NavigationService,
    private translatedToastrService: TranslatedToastrService,
    private translateService: TranslateService,
    private authService: AuthenticationService
  ) {
    super(route, new TruckJourneyDetail());
    this.isLoading = this.loader.getObservable().pipe(map(loadParam => loadParam.loading));
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.originalLoadIds = this.entity.loads ? this.entity.loads.map(load => load.loadId) : [];
    this.route.queryParams.subscribe(({carrierId, assignLoads}): void => {

      this.carrierId = carrierId;
      iif(
        () => this.authService.canManageGlobalTruckJourneys() && carrierId != null,
        this.carrierService.getById(carrierId),
        this.authService.userProfileBehavior().asObservable().pipe(map(userProfile => userProfile.carrier))
      ).pipe(map(carrier => carrier.region.id)).subscribe(regionId => this.regionId = regionId);

      if (assignLoads === 'true') {
        this.isShowingLoadAssignment = true;
      }
    });
  }

  public addLoads(): void {
    this.isShowingLoadAssignment = true;
  }

  public moveLoadToNewPosition(currentLoadIndex: number, newLoadIndex: number): void {
    const loadToMove = this.entity.loads.splice(currentLoadIndex, 1)[0];
    const loadEditToMove = this.entity.loadEdits.splice(currentLoadIndex, 1)[0];
    if (loadToMove && loadEditToMove) {
      this.entity.loads.splice(newLoadIndex, 0, loadToMove);
      this.entity.loadEdits.splice(newLoadIndex, 0, loadEditToMove);
      if (this.editingLoadIndex === currentLoadIndex) {
        this.editingLoadIndex = newLoadIndex;
      }
      this.truckjourneyForm.form.markAsDirty();
    } else {
      this.translatedToastrService.showErrorMessage(
        'truck-journeys.detail.movement-error',
        {pos1: (currentLoadIndex + 1), pos2: (newLoadIndex + 1)}
      );
    }
  }

  public deleteLoad(loadIndex: number): void {
    this.entity.loads.splice(loadIndex, 1);
    this.entity.loadEdits.splice(loadIndex, 1);
    this.computeUnassignedLoadsNumber();
    this.truckjourneyForm.form.markAsDirty();
  }

  public editLoad(loadIndex: number): void {
    this.isEditingOrder = true;
    if (!this.preventNavigationFrom || confirm(this.translateService.instant('common.confirm-lose-changes'))) {
      this.editingLoadIndex = loadIndex;
      this.preventNavigationFrom = false;
    }
  }

  public changeEditStatus(newEditingStatus: boolean): void {
    this.preventNavigationFrom = newEditingStatus
  }

  public stopEditLoad(): void {
    this.isEditingOrder = false;
    this.editingLoadIndex = -1;
    this.preventNavigationFrom = false;
  }

  public applyEditLoadChanges(loadIndex: number): void {
    this.isEditingOrder = false;
    const loadToUpdate = this.entity.loads[loadIndex];
    const loadEdit = this.entity.loadEdits[loadIndex];
    if (loadToUpdate.loadId === loadEdit.loadId) {
      const updatedStops = TruckJourneyLoadMapper.fromDeliveriesEditToStops(loadEdit.deliveries);
      if (!isEqual(updatedStops, loadToUpdate.stops)) {
        loadToUpdate.stops = updatedStops;
        this.truckjourneyForm.form.markAsDirty();
      }
    } else {
      this.translatedToastrService.showErrorMessage('truck-journeys.detail.load-changes-error');
    }
  }

  public cancelAndExit(): void {
    this.appModal.open({
      size: 'confirm',
      zIndex: 2010,
      contentClass: null,
      title: this.translateService.instant('truck-journeys.detail.cancel.title'),
      content: null,
      data: null,
      closeButton: {
        text: this.translateService.instant('truck-journeys.detail.cancel.close')
      },
      validateButton: {
        text: this.translateService.instant('truck-journeys.detail.cancel.confirm'),
        action: () => this.navigateBackToTruckJourneySearchPage()
      }
    });
  }

  public delete(): void {
    this.appModal.open({
      size: 'confirm',
      zIndex: 2010,
      contentClass: null,
      title: this.translateService.instant('truck-journeys.detail.delete-modal.title'),
      content: this.deleteModalContent,
      data: null,
      closeButton: {
        text: this.translateService.instant('truck-journeys.detail.button.no-cancel')
      },
      validateButton: {
        type: 'danger',
        text: this.translateService.instant('truck-journeys.detail.button.yes-confirm-deletion'),
        icon: 'fa fa-trash',
        action: () => {
          this.truckJourneyService.deleteTruckJourney(this.entity.id)
            .pipe(finalize(() => this.loader.stopLoad()))
            .subscribe(() => {
              this.translatedToastrService.showSuccessMessage('truck-journeys.detail.delete-success');
              this.navigateBackToTruckJourneySearchPage();
            }, error => {
              this.errorHandler.handleHttpError(error, 'truck-journeys.detail.delete-error');
            });
        }
      }
    });
  }

  public save(): void {
    this.loader.startLoad(true);
    if (this.isCreation) {
      this.truckJourneyService.createTruckJourney(
        TruckJourneyRequestMapper.fromTruckJourneyDetail(this.entity, this.carrierId)
      )
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(truckJourneyId => {
            if (!truckJourneyId) {
              this.translatedToastrService.showErrorMessage('truck-journeys.detail.create-error');
            } else {
              this.translatedToastrService.showSuccessMessage('truck-journeys.detail.create-success');
              this.navigation.truckJourneys()
                .detail(truckJourneyId, this.carrierId)
                .go();
            }
          },
          error => this.errorHandler.handleHttpError(error, 'truck-journeys.detail.create-error'));
    } else {
      this.truckJourneyService.updateTruckJourney(
        TruckJourneyRequestMapper.fromTruckJourneyDetail(this.entity, this.carrierId)
      )
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(savedTruckJourney => {
            if (!savedTruckJourney) {
              this.translatedToastrService.showErrorMessage('truck-journeys.detail.edit-error');
            } else {
              this.translatedToastrService.showSuccessMessage('truck-journeys.detail.edit-success');
              this.entity = savedTruckJourney;
              this.originalLoadIds = this.entity.loads ? this.entity.loads.map(load => load.loadId) : [];
              this.computeUnassignedLoadsNumber();
            }
          },
          error => this.errorHandler.handleHttpError(error, 'truck-journeys.detail.edit-error')
        );
    }
    this.truckjourneyForm.form.markAsPristine();
  }

  public assignLoadsToTruckJourney(): void {
    if (this.loadAssignments?.length > 0) {
      this.truckjourneyForm.form.markAsDirty();
    }
    if (this.entity.loads) {
      const existingLoadIds = this.entity.loads.map(load => load.loadId);
      this.entity.loads.push(...this.loadAssignments.filter(load => existingLoadIds.every(existingLoadId => existingLoadId !== load.loadId)));
    } else {
      this.entity.loads = [];
      this.entity.loads.push(...this.loadAssignments);
    }
    this.computeUnassignedLoadsNumber();
    this.entity.loadEdits = this.createLoadEditsFromLoads();
    this.loadAssignments = [];

  }

  private createLoadEditsFromLoads(): TruckJourneyLoadEdit[] {
    const newLoadEdits: TruckJourneyLoadEdit[] = [];
    this.entity.loads.forEach(load => {
      const existingLoadEdit = this.entity.loadEdits?.find(loadEdit => loadEdit.loadId === load.loadId);
      newLoadEdits.push(existingLoadEdit ? existingLoadEdit : TruckJourneyLoadMapper.fromLoadtoLoadEdit(load));
    });
    return newLoadEdits;
  }

  private computeUnassignedLoadsNumber(): void {
    this.unassignedLoadsNumber = this.originalLoadIds.filter(originalId =>
      (!this.entity.loads.some(load => load.loadId === originalId))
    ).length;
  }

  public exitDetail(): void {
    if (this.isCreation) {
      this.cancelAndExit();
    } else {
      if (this.truckjourneyForm.form.dirty) {
        this.appModal.open({
          size: 'confirm',
          zIndex: 2010,
          contentClass: null,
          title: this.translateService.instant('truck-journeys.detail.go-back.title'),
          content: this.goBackModalContent,
          data: null,
          closeButton: {
            text: this.translateService.instant('truck-journeys.detail.go-back.cancel')
          },
          validateButton: {
            text: this.translateService.instant('truck-journeys.detail.go-back.confirm'),
            action: () => this.navigateBackToTruckJourneySearchPage()
          }
        });
      } else {
        this.navigateBackToTruckJourneySearchPage();
      }
    }
  }

  private navigateBackToTruckJourneySearchPage(): void {
    this.navigation.truckJourneys()
      .search(this.carrierId, this.regionId)
      .go()
  }

  public isSaveDisabled(): boolean {
    if (!this.truckjourneyForm) {
      return true;
    }
    if (this.isCreation) {
      return this.truckjourneyForm.invalid || this.isEditingOrder;
    }
    return this.truckjourneyForm.invalid || this.isEditingOrder || this.truckjourneyForm.pristine
  }

  public getTruckJourneyContextLabel(): string {
    return (this.isCreation ? '' : '#' + (this.entity.rank + 1) + ' - ')
      + this.entity.vehicle + ' - ' + moment(this.entity.plannedPickupDate).format('DD/MM/YY');
  }
}
